import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { CatalogCard } from '../components/CatalogCard';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  User, 
  MapPin, 
  Star, 
  Calendar,
  Package,
  ShoppingBag,
  CheckCircle,
  Crown,
  ArrowLeft,
  MessageSquare,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';
import { TrustBadge } from '../components/TrustScoreDisplay';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicProfilePage() {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingListings, setLoadingListings] = useState(true);

  useEffect(() => {
    fetchProfile();
    fetchListings();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/profile/${userId}`);
      setProfile(res.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchListings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/profile/${userId}/listings`);
      setListings(res.data.listings || []);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    } finally {
      setLoadingListings(false);
    }
  };

  const memberSince = profile?.created_at 
    ? formatDistanceToNow(new Date(profile.created_at), { addSuffix: false })
    : null;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-[var(--theme-bg)] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#C9956C]" />
        </div>
      </Layout>
    );
  }

  if (!profile) {
    return (
      <Layout>
        <div className="min-h-screen bg-[var(--theme-bg)] flex items-center justify-center">
          <div className="text-center">
            <User className="w-16 h-16 mx-auto text-[var(--theme-text-muted)] mb-4" />
            <h2 className="text-xl font-semibold text-[var(--theme-text-primary)]">User Not Found</h2>
            <p className="text-[var(--theme-text-secondary)] mt-2">This profile doesn't exist or has been removed.</p>
            <Link to="/" className="mt-4 inline-block">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const isOwnProfile = currentUser?.id === userId;

  return (
    <Layout>
      <div className="min-h-screen bg-[var(--theme-bg)]">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Back Button */}
          <button 
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {/* Profile Header Card */}
          <div className="bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] overflow-hidden mb-8">
            {/* Cover Gradient */}
            <div className="h-24 bg-gradient-to-r from-[#1E3A5F] to-[#C9956C]" />
            
            {/* Profile Info */}
            <div className="px-6 pb-6">
              {/* Avatar */}
              <div className="relative -mt-12 mb-4">
                <div className="w-24 h-24 rounded-full bg-[var(--theme-card)] border-4 border-[var(--theme-card)] overflow-hidden">
                  {profile.profile_image ? (
                    <img 
                      src={profile.profile_image} 
                      alt={profile.username}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#1E3A5F] to-[#C9956C] flex items-center justify-center">
                      <span className="text-3xl font-bold text-white">
                        {profile.username?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                </div>
                
                {profile.is_premium && (
                  <div className="absolute bottom-0 right-0 w-8 h-8 bg-[#C9956C] rounded-full flex items-center justify-center border-2 border-[var(--theme-card)]">
                    <Crown className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>

              {/* Name & Username */}
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] flex items-center gap-2">
                    {profile.first_name || profile.username}
                    {profile.is_premium && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-[#C9956C]/20 text-[#C9956C] rounded-full">
                        WANTD+
                      </span>
                    )}
                  </h1>
                  <p className="text-[var(--theme-text-secondary)]">@{profile.username}</p>
                </div>

                {!isOwnProfile && currentUser && (
                  <Link to={`/messages?user=${userId}`}>
                    <Button variant="outline" size="sm">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Message
                    </Button>
                  </Link>
                )}
              </div>

              {/* Stats Row */}
              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-[var(--theme-text-secondary)]">
                {profile.location_name && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-[#C9956C]" />
                    <span>{profile.location_name}</span>
                  </div>
                )}
                
                {memberSince && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-[#C9956C]" />
                    <span>Member for {memberSince}</span>
                  </div>
                )}

                {profile.total_reviews > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <span>{profile.average_rating} ({profile.total_reviews} reviews)</span>
                  </div>
                )}
              </div>

              {/* Trust Badge */}
              {profile.trust_score && (
                <div className="mt-4">
                  <TrustBadge score={profile.trust_score} size="md" />
                </div>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)] p-4 text-center">
              <Package className="w-6 h-6 mx-auto text-[#1E3A5F] dark:text-[#C9956C] mb-2" />
              <div className="text-2xl font-bold text-[var(--theme-text-primary)]">{profile.active_listings || 0}</div>
              <div className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wide">Active Listings</div>
            </div>
            
            <div className="bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)] p-4 text-center">
              <CheckCircle className="w-6 h-6 mx-auto text-[#059669] mb-2" />
              <div className="text-2xl font-bold text-[var(--theme-text-primary)]">{profile.items_sold || 0}</div>
              <div className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wide">Items Sold</div>
            </div>
            
            <div className="bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)] p-4 text-center">
              <ShoppingBag className="w-6 h-6 mx-auto text-[#C9956C] mb-2" />
              <div className="text-2xl font-bold text-[var(--theme-text-primary)]">{profile.active_wants || 0}</div>
              <div className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wide">Active Wants</div>
            </div>
          </div>

          {/* Listings Section */}
          <div>
            <h2 className="text-lg font-semibold text-[var(--theme-text-primary)] mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-[#C9956C]" />
              {profile.first_name || profile.username}'s Listings
              {profile.active_listings > 0 && (
                <span className="text-sm font-normal text-[var(--theme-text-muted)]">
                  ({profile.active_listings})
                </span>
              )}
            </h2>

            {loadingListings ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-[var(--theme-card)] rounded-xl overflow-hidden border border-[var(--theme-border)]">
                    <div className="aspect-square skeleton" />
                    <div className="p-3 space-y-2">
                      <div className="h-4 skeleton rounded" />
                      <div className="h-3 skeleton rounded w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : listings.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {listings.map(item => (
                  <CatalogCard key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)] p-8 text-center">
                <Package className="w-12 h-12 mx-auto text-[var(--theme-text-muted)] mb-3" />
                <p className="text-[var(--theme-text-secondary)]">No active listings</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
